import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WalkCarousel = ({ children, className = '' }) => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isResetting = useRef(false);

  const childCount = React.Children.count(children);
  const childArray = React.Children.toArray(children);
  if (childCount === 0) return null;

  // Gap between cards in pixels
  const GAP = 32;

  // Get the width of one card + gap
  const getCardStep = () => {
    const el = scrollRef.current;
    if (!el) return 300;
    const card = el.querySelector('[data-carousel-item]');
    return card ? card.offsetWidth + GAP : 300;
  };

  // Get the total width of the original card set
  const getSetWidth = () => getCardStep() * childCount;

  // On scroll: detect when we've scrolled into the cloned region and silently reset
  const handleScroll = useCallback(() => {
    if (isResetting.current) return;
    const el = scrollRef.current;
    if (!el) return;

    const setWidth = getSetWidth();
    const scrollPos = el.scrollLeft;

    // If scrolled past the original set (into the clone), jump back
    if (scrollPos >= setWidth) {
      isResetting.current = true;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = scrollPos - setWidth;
      el.style.scrollBehavior = '';
      requestAnimationFrame(() => { isResetting.current = false; });
    }
    // If scrolled before the start (into the prepended clone), jump forward
    else if (scrollPos <= 0) {
      isResetting.current = true;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = scrollPos + setWidth;
      el.style.scrollBehavior = '';
      requestAnimationFrame(() => { isResetting.current = false; });
    }

    // Update active dot index
    const cardStep = getCardStep();
    const idx = Math.round(el.scrollLeft / cardStep) % childCount;
    setActiveIndex(idx);
  }, [childCount]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Start scrolled to the first card of the "real" set (past the prepended clone)
    const setWidth = getSetWidth();
    el.style.scrollBehavior = 'auto';
    el.scrollLeft = setWidth;
    el.style.scrollBehavior = '';

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll, childCount]);

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = getCardStep();
    el.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  // Render: [clone set] [original set] [clone set]
  const renderCard = (child, i, keyPrefix) => (
    <div
      key={`${keyPrefix}-${i}`}
      data-carousel-item
      className="flex-shrink-0 w-[calc(100%-3rem)] sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.34rem)]"
    >
      {child}
    </div>
  );

  return (
    <div className={`relative group ${className}`}>
      {/* Scroll container */}
      <div
        ref={scrollRef}
        className="flex gap-8 overflow-x-auto scroll-smooth scrollbar-hide"
        style={{
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Prepended clone */}
        {childArray.map((child, i) => renderCard(child, i, 'pre'))}
        {/* Original set */}
        {childArray.map((child, i) => renderCard(child, i, 'orig'))}
        {/* Appended clone */}
        {childArray.map((child, i) => renderCard(child, i, 'post'))}
      </div>

      {/* Left arrow — always visible on desktop */}
      <button
        onClick={() => scroll(-1)}
        className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 items-center justify-center rounded-full bg-white shadow-lg border border-gray-200 text-gray-600 hover:text-gray-900 hover:shadow-xl transition-all z-10"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      {/* Right arrow — always visible on desktop */}
      <button
        onClick={() => scroll(1)}
        className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 items-center justify-center rounded-full bg-white shadow-lg border border-gray-200 text-gray-600 hover:text-gray-900 hover:shadow-xl transition-all z-10"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dot indicators */}
      {childCount > 1 && (
        <div className="flex justify-center gap-1.5 mt-6">
          {childArray.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                const el = scrollRef.current;
                if (!el) return;
                const setWidth = getSetWidth();
                const cardStep = getCardStep();
                el.scrollTo({ left: setWidth + i * cardStep, behavior: 'smooth' });
              }}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === activeIndex ? 'bg-purple-600' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}

      {/* Hide scrollbar CSS */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default WalkCarousel;
